import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import type { PortalNotice } from "@/features/portal/api";
import { formatPortalDate } from "@/features/portal/portal-format";
import { portalHref } from "@/features/portal/portal-base";
import { cn } from "@/lib/utils";

const STATUS_LABEL: Record<PortalNotice["status"], string> = {
  OPEN: "Open",
  IN_PROGRESS: "In progress",
  RESOLVED: "Resolved",
};

function statusPill(status: PortalNotice["status"]): string {
  // Light-background pills stay legible on the navy panel (dark text on a light
  // chip), so we reuse the same soft tokens the Notices page uses.
  return status === "RESOLVED"
    ? "bg-success-soft text-success"
    : status === "IN_PROGRESS"
      ? "bg-info-soft text-info"
      : "bg-warning-soft text-warning";
}

/** Navy case-status panel — the real-data equivalent of the mockup's "IRS
 * balance" card, built from the client's logged IRS notices. */
export function NoticesPanel({
  notices,
  firmName,
  phone,
}: {
  notices: PortalNotice[];
  firmName: string;
  phone: string;
}) {
  const open = notices.filter((n) => n.status !== "RESOLVED");
  const firm = firmName || "Your tax team";

  return (
    <div className="bg-brand-panel relative flex flex-col gap-5 overflow-hidden rounded-xl p-7 text-white">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-brand-panel-muted text-xs font-semibold tracking-wider uppercase">
            Your IRS notices
          </div>
          <div className="font-heading mt-2 text-4xl leading-none font-bold">
            {notices.length === 0 ? "All clear" : open.length}
          </div>
          <div className="text-brand-panel-muted mt-1.5 text-sm">
            {notices.length === 0
              ? "No IRS notices on file — your tax team is watching for you."
              : open.length === 0
                ? "Every notice on file is resolved. Nice."
                : `${open.length} open · ${firm} is handling ${open.length === 1 ? "it" : "them"} for you`}
          </div>
        </div>
        {notices.length > 0 && (
          <span className="rounded-full bg-white/12 px-3 py-1.5 text-xs font-semibold whitespace-nowrap">
            {open.length > 0 ? "Being handled" : "Up to date"}
          </span>
        )}
      </div>

      {notices.length > 0 && (
        <div className="grid gap-2.5 sm:grid-cols-3">
          {notices.slice(0, 3).map((n) => (
            <div key={n.id} className="flex flex-col gap-1.5 rounded-lg bg-white/8 p-3.5">
              <div className="text-brand-panel-muted text-xs">
                {n.taxYear ? `Tax year ${n.taxYear}` : "Notice"}
              </div>
              <div className="font-heading text-lg font-bold">{n.code}</div>
              <span
                className={cn(
                  "w-fit rounded-full px-2 py-0.5 text-[11px] font-semibold",
                  statusPill(n.status),
                )}
              >
                {STATUS_LABEL[n.status]}
              </span>
              {n.responseDeadline && (
                <div className="text-brand-panel-muted text-xs">
                  Reply by {formatPortalDate(n.responseDeadline)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 rounded-lg bg-white/8 p-4">
        <div className="text-sm">
          <strong>{firm}</strong> is on your case.{" "}
          <span className="text-brand-panel-muted">
            Questions about a letter? We're one call away.
          </span>
        </div>
        {phone ? (
          <a
            href={`tel:${phone}`}
            className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-sm bg-white/14 px-3.5 py-2 text-sm font-bold text-white hover:bg-white/20"
          >
            <Phone className="size-4" />
            Call us
          </a>
        ) : (
          <Link
            to={portalHref("/notices")}
            className="ml-auto inline-flex shrink-0 items-center rounded-sm bg-white/14 px-3.5 py-2 text-sm font-bold text-white hover:bg-white/20"
          >
            View notices
          </Link>
        )}
      </div>
    </div>
  );
}
